import { reactive } from 'vue';

export type NumberFormat = 'dot' | 'comma';
export type CurrencyPosition = 'left' | 'right';

/** Number/currency display config, driven by the user's per-device preferences
 *  (see useAppStore). Reactive so any component formatting money in its template
 *  re-renders when a preference changes. `numberFormat` picks the grouping /
 *  decimal separators; `currencyPosition` places the symbol left or right. */
export const numberConfig = reactive<{
  numberFormat: NumberFormat;
  currencyPosition: CurrencyPosition;
  currency: string;
}>({
  numberFormat: 'comma',
  currencyPosition: 'left',
  currency: 'EUR',
});

// A locale that produces each separator style ('.' groups + ',' decimal, or vice versa).
const NUMBER_LOCALE: Record<NumberFormat, string> = { dot: 'en-US', comma: 'de-DE' };

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF' };

/** Parse a decimal string (from Laravel's decimal cast) into a number. */
export function parseDecimal(value: string | number | null | undefined): number {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a plain number (no currency) using the preferred separator style. */
export function formatNumber(amount: number | string | null | undefined, decimals = 2): string {
  const value = typeof amount === 'string' ? parseDecimal(amount) : (amount ?? 0);
  return new Intl.NumberFormat(NUMBER_LOCALE[numberConfig.numberFormat], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format an amount for on-screen display, honouring the number style and the
 *  currency-symbol position preferences. `opts` still overrides per call. */
export function formatMoney(
  amount: number | string | null | undefined,
  opts: {
    numberFormat?: NumberFormat;
    currency?: string;
    currencyPosition?: CurrencyPosition;
  } = {},
): string {
  const style = opts.numberFormat ?? numberConfig.numberFormat;
  const currency = opts.currency ?? numberConfig.currency;
  const position = opts.currencyPosition ?? numberConfig.currencyPosition;
  const value = typeof amount === 'string' ? parseDecimal(amount) : (amount ?? 0);
  const num = new Intl.NumberFormat(NUMBER_LOCALE[style], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  return position === 'right' ? `${num} ${symbol}` : `${symbol}${num}`;
}

/** Format a UNIT PRICE with up to 4 decimals (min 2) + currency symbol. Unit
 *  prices carry finer precision than money totals — a line total is still
 *  rounded to 2dp (round2), so use `formatMoney` for totals and this for
 *  per-unit prices. */
export function formatUnitPrice(
  amount: number | string | null | undefined,
  opts: {
    numberFormat?: NumberFormat;
    currency?: string;
    currencyPosition?: CurrencyPosition;
  } = {},
): string {
  const style = opts.numberFormat ?? numberConfig.numberFormat;
  const currency = opts.currency ?? numberConfig.currency;
  const position = opts.currencyPosition ?? numberConfig.currencyPosition;
  const value = typeof amount === 'string' ? parseDecimal(amount) : (amount ?? 0);
  const num = new Intl.NumberFormat(NUMBER_LOCALE[style], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  return position === 'right' ? `${num} ${symbol}` : `${symbol}${num}`;
}

/** Format a QUANTITY with up to 4 decimals (e.g. 24.6532 kg), trailing zeros
 *  trimmed so whole units read as "5", not "5.0000". */
export function formatQuantity(amount: number | string | null | undefined): string {
  const value = typeof amount === 'string' ? parseDecimal(amount) : (amount ?? 0);
  return new Intl.NumberFormat(NUMBER_LOCALE[numberConfig.numberFormat], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(value);
}

/** Round to 2 decimal places safely (avoids float drift on payment matching). */
export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
